import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CmsFormPanel, PanelContent, PanelHeader, PanelTitle } from '@/components/shared/CmsFormPanel';
import { CheckCircle } from 'lucide-react';
import { ConfirmDialog, EntityFormDialog, RegistrationTimeline } from '@/components/shared';
import {
  getBibNumberingModeDescription,
  getBibNumberingModeLabel,
  getBibNumberingUniquenessHint,
  normalizeBibNumberingConfig,
} from '@/utils/bibNumbering';
import { useTshirtSizes } from '@/hooks/useTshirtSizes';

interface RegistrationsFormDialogProps {
  isConfirmDialogOpen: boolean;
  setIsConfirmDialogOpen: (open: boolean) => void;
  confirmingRegistration: any;
  handleConfirm: (paymentStatus: string) => void;
  confirmRegistrationPending: boolean;
  isDetailDialogOpen: boolean;
  setIsDetailDialogOpen: (open: boolean) => void;
  selectedRegistration: any;
  isBibDialogOpen: boolean;
  setIsBibDialogOpen: (open: boolean) => void;
  editingBibRegistration: any;
  bibNumberInput: string;
  setBibNumberInput: (value: string) => void;
  handleSaveBibNumber: () => void;
  updateBibNumberPending: boolean;
  getRaceName: (raceId: any) => string;
  getRaceTypeName: (raceTypeId: any) => string;
  getRaceTypeDetails: (raceTypeId: any) => any;
  getDiscountInfo: (reg: any) => { discountText: string | null; discountAmount: number };
  getFinalPrice: (reg: any) => number;
  raceTypePrices: Record<string, number>;
  deleteTarget: any;
  setDeleteTarget: (target: any) => void;
  confirmDelete: () => void;
  // Create/Edit
  isEditDialogOpen: boolean;
  setIsEditDialogOpen: (open: boolean) => void;
  editingRegistration: any;
  registrationFormData: any;
  setRegistrationFormData: (data: any) => void;
  handleSubmitRegistration: (e: React.FormEvent) => void;
  handleCloseEditDialog: () => void;
  races: any[];
  raceTypes: Record<string, any[]>;
}

const helperClassName = 'text-sm text-muted-foreground';

const RegistrationsFormDialog: React.FC<RegistrationsFormDialogProps> = ({
  isConfirmDialogOpen, setIsConfirmDialogOpen, confirmingRegistration,
  handleConfirm, confirmRegistrationPending,
  isDetailDialogOpen, setIsDetailDialogOpen, selectedRegistration,
  isBibDialogOpen, setIsBibDialogOpen, editingBibRegistration, bibNumberInput, setBibNumberInput,
  handleSaveBibNumber, updateBibNumberPending,
  getRaceName, getRaceTypeName, getRaceTypeDetails, getDiscountInfo, getFinalPrice, raceTypePrices,
  deleteTarget, setDeleteTarget, confirmDelete,
  isEditDialogOpen, setIsEditDialogOpen, editingRegistration, registrationFormData, setRegistrationFormData,
  handleSubmitRegistration, handleCloseEditDialog,
  races, raceTypes,
}) => {
  const { data: tshirtSizes = [] } = useTshirtSizes(registrationFormData?.raceId, registrationFormData?.raceTypeId || undefined);

  const confirmingRaceType = confirmingRegistration ? getRaceTypeDetails(confirmingRegistration.raceTypeId) : null;
  const confirmingNumbering = normalizeBibNumberingConfig(confirmingRaceType);

  const selectedRaceType = selectedRegistration ? getRaceTypeDetails(selectedRegistration.raceTypeId) : null;
  const selectedNumbering = normalizeBibNumberingConfig(selectedRaceType);

  const editingRaceType = editingBibRegistration ? getRaceTypeDetails(editingBibRegistration.raceTypeId) : null;
  const editingNumbering = normalizeBibNumberingConfig(editingRaceType);

  const detailBibText = !selectedRegistration
    ? '—'
    : selectedRegistration.bibNumber != null
      ? '#' + selectedRegistration.bibNumber
      : selectedNumbering.bibNumberingEnabled
        ? 'Недоделен'
        : 'Без број';

  return (
    <>
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Потврди регистрација</DialogTitle></DialogHeader>
          {confirmingRegistration && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Учесник</p>
                <p className="font-semibold text-lg">{confirmingRegistration.firstName} {confirmingRegistration.lastName}</p>
                <p className="text-sm text-muted-foreground">{confirmingRegistration.email}</p>
                <p className="text-sm text-muted-foreground mt-1">Трка: {getRaceName(confirmingRegistration.raceId)} &bull; Тип: {getRaceTypeName(confirmingRegistration.raceTypeId)}</p>
              </div>
              <p className={helperClassName}>
                {confirmingNumbering.bibNumberingEnabled
                  ? 'По потврдувањето системот ќе провери или додели валиден стартен број според конфигурацијата на типот.'
                  : 'Овој тип е конфигуриран без стартни броеви, па регистрацијата ќе биде потврдена без број.'}
              </p>
              <p className="text-xs text-muted-foreground">{getBibNumberingModeDescription(confirmingNumbering)}</p>
              <div className="flex flex-col gap-3">
                <Button onClick={() => handleConfirm('Paid')} disabled={confirmRegistrationPending} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <CheckCircle className="w-4 h-4 mr-2" />{confirmRegistrationPending ? 'Потврдување...' : 'Потврди со плаќање'}
                </Button>
                <Button onClick={() => handleConfirm('Pending')} disabled={confirmRegistrationPending} variant="outline" className="w-full border-yellow-500 text-yellow-700 hover:bg-yellow-50">
                  <CheckCircle className="w-4 h-4 mr-2" />{confirmRegistrationPending ? 'Потврдување...' : 'Потврди без плаќање'}
                </Button>
                <Button onClick={() => setIsConfirmDialogOpen(false)} disabled={confirmRegistrationPending} variant="outline" className="w-full">Откажи</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isBibDialogOpen} onOpenChange={setIsBibDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Измени стартен број</DialogTitle></DialogHeader>
          {editingBibRegistration && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <p className="font-semibold text-lg">{editingBibRegistration.firstName} {editingBibRegistration.lastName}</p>
                <p className="text-sm text-muted-foreground">{getRaceName(editingBibRegistration.raceId)} &bull; {getRaceTypeName(editingBibRegistration.raceTypeId)}</p>
                <p className="text-xs text-muted-foreground mt-2">Режим: {getBibNumberingModeLabel(editingNumbering)}</p>
              </div>
              <div>
                <Label htmlFor="bibNumberInput">Стартен број</Label>
                <Input
                  id="bibNumberInput"
                  inputMode="numeric"
                  value={bibNumberInput}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (/^\d*$/.test(nextValue)) {
                      setBibNumberInput(nextValue);
                    }
                  }}
                  placeholder={editingNumbering.bibNumberingEnabled ? 'Пр. 152' : 'Оставете празно за бришење'}
                />
                <p className="mt-2 text-xs text-muted-foreground">{getBibNumberingUniquenessHint(editingNumbering)}</p>
                <p className="mt-1 text-xs text-muted-foreground">Оставете празно само кога типот е без нумерација или кога backend го дозволува бришењето за оваа регистрација.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsBibDialogOpen(false)} disabled={updateBibNumberPending}>Откажи</Button>
                <Button type="button" onClick={handleSaveBibNumber} disabled={updateBibNumberPending}>
                  {updateBibNumberPending ? 'Зачувување...' : 'Зачувај'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CmsFormPanel open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <PanelContent>
          <PanelHeader><PanelTitle>Детали на регистрација</PanelTitle></PanelHeader>
          {selectedRegistration && (
            <div className="space-y-4 mt-4">
              <RegistrationTimeline
                registrationStatus={selectedRegistration.registrationStatus}
                paymentStatus={selectedRegistration.paymentStatus}
                bibPickedUp={selectedRegistration.bibPickedUp}
                createdAt={selectedRegistration.createdAt}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Стартен број</Label>
                  <p className="font-semibold">{detailBibText}</p>
                  <p className="text-xs text-muted-foreground mt-1">{getBibNumberingModeDescription(selectedNumbering)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Статус</Label>
                  <p className="font-semibold">{selectedRegistration.registrationStatus}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Режим на нумерација</Label>
                  <p>{getBibNumberingModeLabel(selectedNumbering)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Преземање број</Label>
                  <p>
                    {!selectedNumbering.bibNumberingEnabled
                      ? 'Не се користи'
                      : selectedRegistration.bibNumber == null
                        ? 'Нема доделен број'
                        : selectedRegistration.bibPickedUp
                          ? 'Преземено'
                          : 'Не е преземено'}
                  </p>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Лични податоци</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-muted-foreground">Име</Label><p>{selectedRegistration.firstName}</p></div>
                  <div><Label className="text-muted-foreground">Презиме</Label><p>{selectedRegistration.lastName}</p></div>
                  <div><Label className="text-muted-foreground">Email</Label><p>{selectedRegistration.email}</p></div>
                  <div><Label className="text-muted-foreground">Телефон</Label><p>{selectedRegistration.phone}</p></div>
                  <div><Label className="text-muted-foreground">Итен контакт</Label><p>{selectedRegistration.emergencyContactPhone || '-'}</p></div>
                  <div><Label className="text-muted-foreground">Град</Label><p>{selectedRegistration.city}</p></div>
                  <div><Label className="text-muted-foreground">Држава</Label><p>{selectedRegistration.country}</p></div>
                  <div><Label className="text-muted-foreground">Пол</Label><p>{selectedRegistration.gender}</p></div>
                  <div><Label className="text-muted-foreground">Датум на раѓање</Label><p>{selectedRegistration.dateOfBirth}</p></div>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Детали на учество</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-muted-foreground">Трка</Label><p>{getRaceName(selectedRegistration.raceId)}</p></div>
                  <div><Label className="text-muted-foreground">Тип на трка</Label><p>{getRaceTypeName(selectedRegistration.raceTypeId)}</p></div>
                  <div><Label className="text-muted-foreground">Тип на учесник</Label><p>{selectedRegistration.participantType}</p></div>
                  {selectedRegistration.teamName && <div><Label className="text-muted-foreground">Име на тим</Label><p>{selectedRegistration.teamName}</p></div>}
                  <div><Label className="text-muted-foreground">Величина на маичка</Label><p className="font-semibold bg-muted inline-block px-2 py-1 rounded">{selectedRegistration.tshirtSize || '—'}</p></div>
                  <div>
                    <Label className="text-muted-foreground">Нумерација</Label>
                    <p>{getBibNumberingModeLabel(selectedNumbering)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{getBibNumberingUniquenessHint(selectedNumbering)}</p>
                  </div>
                  {selectedRegistration.discountCode && (
                    <div>
                      <Label className="text-muted-foreground">Код за попуст</Label>
                      <p className="font-mono font-semibold text-purple-700 bg-purple-50 inline-block px-2 py-1 rounded">{selectedRegistration.discountCode}</p>
                      {(() => {
                        const { discountText } = getDiscountInfo(selectedRegistration);
                        return discountText ? <span className="text-xs text-purple-600 ml-2">(-{discountText})</span> : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Плаќање</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-muted-foreground">Оригинална цена</Label><p>{raceTypePrices[selectedRegistration.raceTypeId] || 0} MKD</p></div>
                  <div><Label className="text-muted-foreground">Финална цена</Label><p className="font-semibold text-lg text-green-700">{getFinalPrice(selectedRegistration)} MKD</p></div>
                  <div><Label className="text-muted-foreground">Статус на плаќање</Label><p>{selectedRegistration.paymentStatus}</p></div>
                  <div><Label className="text-muted-foreground">Датум на регистрација</Label><p>{new Date(selectedRegistration.createdAt).toLocaleString('mk-MK')}</p></div>
                </div>
              </div>
            </div>
          )}
        </PanelContent>
      </CmsFormPanel>
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Избриши регистрација"
        description="Дали сте сигурни дека сакате да ја избришете оваа регистрација?"
        onConfirm={confirmDelete}
      />

      <EntityFormDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => { if (!open) handleCloseEditDialog(); }}
        title={editingRegistration ? 'Измени регистрација' : 'Додај нова регистрација'}
        onSubmit={handleSubmitRegistration}
        submitLabel={editingRegistration ? 'Зачувај промени' : 'Додај регистрација'}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-firstName">Име *</Label>
            <Input id="reg-firstName" value={registrationFormData.firstName} onChange={(e) => setRegistrationFormData({ ...registrationFormData, firstName: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="reg-lastName">Презиме *</Label>
            <Input id="reg-lastName" value={registrationFormData.lastName} onChange={(e) => setRegistrationFormData({ ...registrationFormData, lastName: e.target.value })} required />
          </div>
        </div>
        <div>
          <Label htmlFor="reg-email">Email *</Label>
          <Input id="reg-email" type="email" value={registrationFormData.email} onChange={(e) => setRegistrationFormData({ ...registrationFormData, email: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-phone">Телефон</Label>
            <Input id="reg-phone" value={registrationFormData.phone} onChange={(e) => setRegistrationFormData({ ...registrationFormData, phone: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="reg-emergencyPhone">Итен контакт</Label>
            <Input id="reg-emergencyPhone" value={registrationFormData.emergencyContactPhone} onChange={(e) => setRegistrationFormData({ ...registrationFormData, emergencyContactPhone: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-city">Град</Label>
            <Input id="reg-city" value={registrationFormData.city} onChange={(e) => setRegistrationFormData({ ...registrationFormData, city: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="reg-country">Држава</Label>
            <Input id="reg-country" value={registrationFormData.country} onChange={(e) => setRegistrationFormData({ ...registrationFormData, country: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-dob">Датум на раѓање *</Label>
            <Input id="reg-dob" type="date" value={registrationFormData.dateOfBirth} onChange={(e) => setRegistrationFormData({ ...registrationFormData, dateOfBirth: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="reg-gender">Пол</Label>
            <Select value={registrationFormData.gender || 'unset'} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, gender: v === 'unset' ? '' : v })}>
              <SelectTrigger id="reg-gender" className="mt-1"><SelectValue placeholder="Изберете пол" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                <SelectItem value="Male">Машки</SelectItem>
                <SelectItem value="Female">Женски</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-participantType">Тип на учесник</Label>
            <Input id="reg-participantType" value={registrationFormData.participantType} onChange={(e) => setRegistrationFormData({ ...registrationFormData, participantType: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="reg-teamName">Тим</Label>
            <Input id="reg-teamName" value={registrationFormData.teamName} onChange={(e) => setRegistrationFormData({ ...registrationFormData, teamName: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="reg-tshirtSize">Величина на маичка</Label>
            <Select value={registrationFormData.tshirtSize || 'unset'} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, tshirtSize: v === 'unset' ? '' : v })}>
              <SelectTrigger id="reg-tshirtSize" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">—</SelectItem>
                {tshirtSizes.map((s: any) => <SelectItem key={s.id} value={s.size}>{s.size}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="reg-discountCode">Код за попуст</Label>
            <Input id="reg-discountCode" value={registrationFormData.discountCode} onChange={(e) => setRegistrationFormData({ ...registrationFormData, discountCode: e.target.value.toUpperCase() })} />
          </div>
        </div>
        <div>
          <Label htmlFor="reg-raceId">Настан *</Label>
          <Select value={registrationFormData.raceId || 'unset'} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, raceId: v === 'unset' ? '' : v, raceTypeId: '' })}>
            <SelectTrigger id="reg-raceId" className="mt-1"><SelectValue placeholder="Изберете настан" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {races.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="reg-raceTypeId">Тип на трка *</Label>
          <Select value={registrationFormData.raceTypeId || 'unset'} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, raceTypeId: v === 'unset' ? '' : v })}>
            <SelectTrigger id="reg-raceTypeId" className="mt-1"><SelectValue placeholder="Прво изберете настан" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {(raceTypes[registrationFormData.raceId] || []).map((rt: any) => <SelectItem key={rt.id} value={String(rt.id)}>{rt.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="reg-regStatus">Статус на регистрација</Label>
              <Select value={registrationFormData.registrationStatus} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, registrationStatus: v })}>
                <SelectTrigger id="reg-regStatus" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Confirmed">Confirmed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reg-payStatus">Статус на плаќање</Label>
              <Select value={registrationFormData.paymentStatus} onValueChange={(v) => setRegistrationFormData({ ...registrationFormData, paymentStatus: v })}>
                <SelectTrigger id="reg-payStatus" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="reg-cookies" checked={registrationFormData.cookiesConsent} onCheckedChange={(c) => setRegistrationFormData({ ...registrationFormData, cookiesConsent: !!c })} />
            <Label htmlFor="reg-cookies" className="text-sm font-normal cursor-pointer">Согласност за колачиња</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="reg-privacy" checked={registrationFormData.privacyPolicy} onCheckedChange={(c) => setRegistrationFormData({ ...registrationFormData, privacyPolicy: !!c })} />
            <Label htmlFor="reg-privacy" className="text-sm font-normal cursor-pointer">Политика на приватност</Label>
          </div>
        </div>
      </EntityFormDialog>
    </>
  );
};

export default RegistrationsFormDialog;
